import React from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { cn } from "../../lib/utils";

export function AuthInput({ className, type, ...props }) {
    return (
        <input
            type={type}
            className={cn(
                "sign-in-input-field flex h-10 w-full min-w-0 rounded-lg border border-white/10 bg-white/5 px-3 py-1 text-base text-white shadow-sm outline-none transition-[color,box-shadow] placeholder:text-white/30 md:text-sm",
                "focus-visible:border-white/20 focus-visible:ring-[3px] focus-visible:ring-white/20",
                "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
                className
            )}
            {...props}
        />
    );
}

export function AuthSelect({ className, children, ...props }) {
    return (
        <select
            className={cn(
                "sign-in-input-field flex h-10 w-full min-w-0 cursor-pointer appearance-none rounded-lg border border-white/10 bg-white/5 px-3 py-1 text-base text-white shadow-sm outline-none transition-[color,box-shadow] md:text-sm",
                "focus-visible:border-white/20 focus-visible:ring-[3px] focus-visible:ring-white/20",
                "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
                className
            )}
            {...props}
        >
            {children}
        </select>
    );
}

/**
 * Shared 3D glass auth card shell (login / signup).
 */
export function AuthCardShell({
    logoSrc,
    title,
    description,
    children,
    embedded = true,
    maxWidthClass = "max-w-sm",
    onSubmit,
    formClassName,
}) {
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);
    const rotateX = useTransform(mouseY, [-300, 300], [10, -10]);
    const rotateY = useTransform(mouseX, [-300, 300], [-10, 10]);

    const handleMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        mouseX.set(e.clientX - rect.left - rect.width / 2);
        mouseY.set(e.clientY - rect.top - rect.height / 2);
    };

    const handleMouseLeave = () => {
        mouseX.set(0);
        mouseY.set(0);
    };

    const outerClass = embedded
        ? "relative z-10 flex w-full items-center justify-center"
        : "relative flex min-h-screen w-screen items-center justify-center overflow-hidden bg-black";

    const headerAndBody = (
        <>
            <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", duration: 0.8 }}
                className="relative mx-auto mb-4 flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-white/5"
            >
                {logoSrc ? (
                    <img src={logoSrc} alt="YesMLS Pro" className="h-10 w-auto object-contain" />
                ) : (
                    <span className="bg-gradient-to-b from-white to-white/70 bg-clip-text text-lg font-bold text-transparent">
                        Y
                    </span>
                )}
            </motion.div>

            <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mb-1 bg-gradient-to-b from-white to-white/80 bg-clip-text text-center text-xl font-bold text-transparent"
            >
                {title}
            </motion.h1>

            {description ? (
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="mb-5 text-center text-xs text-white/60"
                >
                    {description}
                </motion.p>
            ) : null}

            {children}
        </>
    );

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className={outerClass}
            style={{ perspective: 1500 }}
        >
            <motion.div
                className={cn("relative w-full", maxWidthClass)}
                style={{ rotateX, rotateY }}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                whileHover={{ z: 10 }}
            >
                <div className="group relative">
                    <motion.div
                        className="absolute -inset-px rounded-2xl opacity-0 transition-opacity duration-700 group-hover:opacity-70"
                        animate={{
                            boxShadow: [
                                "0 0 10px 2px rgba(255,255,255,0.03)",
                                "0 0 15px 5px rgba(255,255,255,0.05)",
                                "0 0 10px 2px rgba(255,255,255,0.03)",
                            ],
                        }}
                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", repeatType: "mirror" }}
                    />

                    <motion.div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/50 p-6 shadow-2xl backdrop-blur-xl">
                        {onSubmit ? (
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    onSubmit(e);
                                }}
                                className={formClassName}
                            >
                                {headerAndBody}
                            </form>
                        ) : (
                            <div className={formClassName}>{headerAndBody}</div>
                        )}
                    </motion.div>
                </div>
            </motion.div>
        </motion.div>
    );
}
